/**
 * 诊断流水线 SPA 页面
 * 横向流水线 (Horizontal Pipeline) 设计
 * 渐进式披露 + 免费增值转化漏斗
 * 支持多语言国际化
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  useMutation,
} from "@tanstack/react-query";
import assessmentService, {
  Assessment,
  AssessmentsResponse,
  PrescreenResponse,
} from "@/services/assessmentService";
import { cn } from "@/lib/utils";

// UI Components
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

// Icons
import {
  Upload,
  Brain,
  Check,
  History,
  Trash2,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  ImageIcon,
  X,
} from "lucide-react";

// Shared Components
import ImageUploader, {
  ImageDiagnostics,
} from "@/components/shared/ImageUploader";
import TankSelectionModal from "@/components/assessments/TankSelectionModal";
import SelectedTankCard from "@/components/assessments/SelectedTankCard";
import TypewriterHint, {
  AssessmentStage,
} from "@/components/assessments/TypewriterHint";
import { Tank } from "@/services/tankService";
import { SmartCTABar } from "@/components/care-plan/SmartCTABar";
import { CarePlanTimelinePanel } from "@/components/care-plan/CarePlanTimelinePanel";

// Prescreen Notification Components
import { PrescreenErrorOverlay } from "@/components/assessments/PrescreenErrorOverlay";
import { SoftPassBadge } from "@/components/assessments/SoftPassBadge";
import { UploadCard } from "@/components/assessments/UploadCard";
import {
  mapReasonToCategory,
  shouldShowFeedback,
  getLocalizedMessage,
  isSoftPass,
} from "@/services/prescreenNotificationService";
import { AssessmentInputForm } from "@/components/assessments/AssessmentInputForm";
import { ToolboxItem } from "@/components/assessments/ToolboxSelector";
import {
  PrescreenErrorCategory,
  PrescreenErrorMessage,
  PrescreenErrorState,
  SoftPassState,
} from "@/types/prescreen";

// Data Flywheel Components
import { CacheIndicator } from "@/components/assessments/CacheIndicator";
import { VisualScanCard } from "@/components/assessments/VisualScanCard";
import { ProcessingStatus } from "@/components/assessments/ProcessingStatus";
import { BottomActionBar } from "@/components/shared/BottomActionBar"; // Import new component
import { AssessmentCard } from "@/components/assessments/AssessmentCard";
import { HistoryPanel } from "@/components/assessments/HistoryPanel";
import { AssessmentRecord, ParsedAssessment } from "@/types/assessments";
import { tryParseJson, extractTitle } from "@/lib/assessmentUtils";
import { PhotoExamples } from "@/components/assessments/PhotoExamples";
import { useNavBack } from "@/contexts/NavBackContext";
import { InsufficientPointsDialog } from "@/components/assessments/InsufficientPointsDialog";
import {
  ApiError,
  InsufficientPointsErrorData,
  ErrorCodes,
} from "@/types/apiErrors";

const getSeverityColor = (severity?: string) => {
  switch (severity) {
    case "high":
      return "bg-red-500";
    case "medium":
      return "bg-yellow-500";
    case "low":
      return "bg-green-500";
    default:
      return "bg-gray-400";
  }
};

// 严重程度文本由组件内部通过 i18n 处理

// ==================== API ====================
const PAGE_SIZE = 10;
const ASSESSMENT_COST = 100; // 单次评估消耗积分

import { usePoints } from "@/hooks/usePoints";
import { useCreateAssessment } from "@/hooks/useCreateAssessment";
import { PointsCostIndicator } from "@/components/assessments/PointsCostIndicator";
import { SprayIcon } from "@/components/icons/SprayIcon";

/**
 * 获取诊断历史记录
 * @see Requirements 1.1, 4.4 - 使用 /api/v1/assessments 端点
 */
const fetchHistory = async (
  pageParam: number,
): Promise<{ items: AssessmentRecord[]; hasMore: boolean }> => {
  const response = await assessmentService.getAssessments({
    page: pageParam,
    per_page: PAGE_SIZE,
  });
  const items = response.assessments as AssessmentRecord[];
  return { items, hasMore: items.length === PAGE_SIZE };
};

/**
 * 获取单个诊断记录
 * @see Requirements 1.1 - 使用 /api/v1/assessments/:id 端点
 * @param id - friendly_id 字符串
 * @throws {Error} 当 assessment 不存在时 (404)
 */
const fetchAssessment = async (id: string): Promise<AssessmentRecord> => {
  try {
    const assessment = await assessmentService.getAssessment(id);
    return assessment as AssessmentRecord;
  } catch (error) {
    // 检查是否是 404 错误
    if (error instanceof Error && error.message.includes("404")) {
      const notFoundError = new Error("ASSESSMENT_NOT_FOUND");
      notFoundError.name = "AssessmentNotFound";
      throw notFoundError;
    }
    throw error;
  }
};

// ==================== Sub Components ====================

/** 连接线组件 - 响应式设计 */
const Connector = ({
  completed,
  animate = true,
}: {
  completed: boolean;
  animate?: boolean;
}) => (
  <motion.div
    layout
    transition={{ layout: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } }}
  >
    {/* Desktop: Horizontal Connector */}
    <div className="hidden md:flex items-center justify-center relative h-6 w-8 xl:w-10 shrink-0">
      <div className="absolute inset-0 flex items-center">
        <motion.div
          className={`w-full h-[2px] ${completed ? "bg-border" : "bg-border/50"}`}
          initial={animate ? { scaleX: 0 } : { scaleX: 1 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{ originX: 0 }}
        />
      </div>
      <motion.div
        className={`relative z-10 w-4 h-4 rounded-full flex items-center justify-center ${
          completed
            ? "bg-muted border-2 border-border"
            : "bg-card border-2 border-border/50"
        }`}
        initial={animate ? { scale: 0 } : { scale: 1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.2, delay: 0.3 }}
      >
        {completed ? (
          <Check className="h-2.5 w-2.5 text-muted-foreground" />
        ) : (
          <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
        )}
      </motion.div>
    </div>

    {/* Mobile: Vertical Connector */}
    <div className="md:hidden flex flex-col items-center justify-center h-12 w-full relative py-2">
      <div className="absolute inset-0 flex justify-center">
        <motion.div
          className={`h-full w-[2px] ${completed ? "bg-border" : "bg-border/50"}`}
          initial={animate ? { scaleY: 0 } : { scaleY: 1 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{ originY: 0 }}
        />
      </div>
      <motion.div
        className={`relative z-10 w-5 h-5 rounded-full flex items-center justify-center bg-background ${
          completed ? "border-2 border-border" : "border-2 border-border/50"
        }`}
        initial={animate ? { scale: 0 } : { scale: 1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.2, delay: 0.3 }}
      >
        {completed ? (
          <Check className="h-3 w-3 text-muted-foreground" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />
        )}
      </motion.div>
    </div>
  </motion.div>
);

/** 窗口2：评估结论卡片 (ConclusionCard) */

/** AI 处理状态组件 - 动态文案 */

// ==================== Main Component ====================
const AssessmentPipeline: React.FC = () => {
  const { t } = useTranslation("assessments");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: urlId } = useParams<{ id: string }>();

  // 导航返回按钮配置
  const { setConfig: setNavBackConfig, resetConfig: resetNavBackConfig } =
    useNavBack();

  // 从 URL 参数初始化 selectedId（使用 friendly_id 字符串）
  const initialSelectedId = useMemo((): string => {
    if (!urlId || urlId === "new") return "new";
    return urlId;
  }, [urlId]);

  // 状态 - selectedId 现在是 friendly_id 字符串或 "new"
  const [selectedId, setSelectedIdState] = useState<string>(initialSelectedId);

  const [highlightedFeature, setHighlightedFeature] = useState<number | null>(
    null,
  );
  const [uploadedBlob, setUploadedBlob] = useState<Blob | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");

  const [selectedToolboxItems, setSelectedToolboxItems] = useState<
    ToolboxItem[]
  >([]);
  // 用户问题描述
  const [userQuery, setUserQuery] = useState<string>("");
  const [showHistory, setShowHistory] = useState(false);
  // 鱼缸选择状态
  const [showTankModal, setShowTankModal] = useState(false);
  const [selectedTank, setSelectedTank] = useState<Tank | null>(null);
  // 是否为更换鱼缸模式（已选择过鱼缸后再次打开 Modal）
  const [isTankChangeMode, setIsTankChangeMode] = useState(false);
  // 打字机提示是否准备好（等待组件加载完成）
  const [typewriterReady, setTypewriterReady] = useState(false);
  // 是否显示护理时间轴（替换三阶段卡片）
  const [showTimeline, setShowTimeline] = useState(false);
  // 是否显示照片示例
  const [showPhotoExamples, setShowPhotoExamples] = useState(true);

  // 自动隐藏照片示例 - 4秒后自动消失，当再次显示时也会触发此逻辑
  useEffect(() => {
    if (showPhotoExamples) {
      const timer = setTimeout(() => {
        setShowPhotoExamples(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showPhotoExamples]);

  // 积分相关状态
  const [showInsufficientPointsModal, setShowInsufficientPointsModal] =
    useState(false);
  const [insufficientPointsData, setInsufficientPointsData] =
    useState<InsufficientPointsErrorData | null>(null);
  const [isUnlocking, setIsUnlocking] = useState(false);
  // 记录是否是刚刚上传的新诊断（用于优化打字机提示，避免出现“正在获取历史记录”）
  const [isNewUpload, setIsNewUpload] = useState(false);
  // 记录上一次的状态，用于检测状态变更
  const [prevStatus, setPrevStatus] = useState<string | null>(null);

  // 包装 setSelectedId，同步更新 URL（使用 friendly_id 字符串）
  const setSelectedId = useCallback(
    (id: string) => {
      setSelectedIdState(id);
      // 切换 assessment 时重置 timeline 显示状态
      setShowTimeline(false);
      // 同步 URL，使用 replace 避免产生过多历史记录
      const newPath = id === "new" ? "/assessments/new" : `/assessments/${id}`;
      navigate(newPath, { replace: true });
    },
    [navigate],
  );

  // 积分查询
  const { data: pointsData } = usePoints();
  const balance = pointsData?.balance ?? 0;

  // 创建诊断 Mutation
  const createAssessmentMutation = useCreateAssessment({
    onSuccess: (data) => {
      setIsNewUpload(true);
      setSelectedId(data.friendly_id);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        setInsufficientPointsData(err.data as InsufficientPointsErrorData);
        setShowInsufficientPointsModal(true);
      }
    },
  });

  // Alias for legacy code compatibility
  const isSubmitting = createAssessmentMutation.isPending;

  // 根据当前状态配置返回按钮行为
  useEffect(() => {
    // 新建模式下不显示返回按钮
    if (selectedId === "new") {
      resetNavBackConfig();
      return;
    }

    // 在评估详情页显示返回按钮
    if (showTimeline) {
      // Timeline 阶段：返回到分析报告（关闭 timeline）
      setNavBackConfig({
        enabled: true,
        onBack: () => setShowTimeline(false),
      });
    } else {
      // 分析报告阶段：返回到 new 页面
      setNavBackConfig({
        enabled: true,
        targetPath: "/assessments/new",
      });
    }

    // 组件卸载时重置配置
    return () => {
      resetNavBackConfig();
    };
  }, [selectedId, showTimeline, setNavBackConfig, resetNavBackConfig]);

  // URL 参数变化时同步 selectedId（处理浏览器前进/后退）
  useEffect(() => {
    const newId = !urlId || urlId === "new" ? "new" : urlId;
    if (newId !== selectedId) {
      setSelectedIdState(newId);
      setIsNewUpload(false);
      // 切换 assessment 时重置 timeline 显示状态
      setShowTimeline(false);
    }
  }, [urlId, selectedId]);

  // 组件加载完成后启动打字机效果
  useEffect(() => {
    const timer = setTimeout(() => {
      setTypewriterReady(true);
    }, 500); // 延迟 500ms 等待其他组件渲染完成
    return () => clearTimeout(timer);
  }, []);

  // 历史记录查询
  const {
    data: historyPages,
    isLoading: loadingHistory,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["assessment-history"],
    queryFn: ({ pageParam = 1 }) => fetchHistory(pageParam as number),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5分钟内不重新请求
    gcTime: 10 * 60 * 1000, // 10分钟后清理缓存
  });

  const historyRecords = useMemo(
    () => historyPages?.pages.flatMap((p) => p.items) ?? [],
    [historyPages],
  );

  // 当前诊断详情查询（使用 friendly_id）
  const {
    data: currentAssessment,
    isLoading: loadingAssessment,
    error: assessmentError,
  } = useQuery({
    queryKey: ["assessment", selectedId],
    queryFn: () => fetchAssessment(selectedId),
    enabled: selectedId !== "new",
    retry: (failureCount, error) => {
      // 404 错误不重试
      if (error instanceof Error && error.name === "AssessmentNotFound") {
        return false;
      }
      return failureCount < 3;
    },
    refetchInterval: (query) => {
      const d = query.state.data as AssessmentRecord | undefined;
      const waiting =
        !d ||
        d.status === "processing" ||
        d.status === "pending" ||
        d.status === "pending_analysis" ||
        d.status === "pending_prescreen";
      return waiting ? 3000 : false;
    },
  });

  // 检查是否是 404 错误
  const isAssessmentNotFound = useMemo(
    () =>
      assessmentError instanceof Error &&
      assessmentError.name === "AssessmentNotFound",
    [assessmentError],
  );

  // 监听评估状态变化，显示积分变动通知
  useEffect(() => {
    if (!currentAssessment) {
      setPrevStatus(null);
      return;
    }

    const currentStatus = currentAssessment.status;

    // 检测状态从 processing -> completed (扣除积分)
    if (
      (prevStatus === "processing" || prevStatus === "pending") &&
      currentStatus === "completed"
    ) {
      // 确认确实发生了扣分（有扣分时间或积分金额）
      // @ts-ignore
      const points = currentAssessment.points_amount || 0;
      if (points > 0) {
        toast.success(
          t("assessments:points.deducted", "分析完成，已扣除 {{points}} 积分", {
            points,
          }),
        );
      }
    }

    // 检测状态从 processing -> failed (退款)
    if (
      (prevStatus === "processing" || prevStatus === "pending") &&
      currentStatus === "failed"
    ) {
      // 确认发生了退款（有退款时间）
      // @ts-ignore
      if (currentAssessment.points_refunded_at) {
        // @ts-ignore
        const points = currentAssessment.points_amount || 0;
        toast.error(
          t("assessments:points.refunded", "分析失败，已退还 {{points}} 积分", {
            points,
          }),
        );
      }
    }

    setPrevStatus(currentStatus);
  }, [currentAssessment, prevStatus, t]);

  /**
   * 删除诊断
   * @see Requirements 1.1 - 使用 /api/v1/assessments/:id 端点
   */
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await assessmentService.deleteAssessment(id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["assessment-history"] });
      toast.success("已删除");
      if (selectedId === id) setSelectedId("new");
    },
    onError: () => toast.error("删除失败"),
  });

  // 图片诊断信息
  const [uploadedDiagnostics, setUploadedDiagnostics] =
    useState<ImageDiagnostics | null>(null);
  // 本地预览 URL（提升到父组件避免 key 变化导致丢失）
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  // 预筛选状态
  const [isPrescreening, setIsPrescreening] = useState(false);
  // 预筛选结果（用于显示鱼类信息）
  const [prescreenResult, setPrescreenResult] =
    useState<PrescreenResponse | null>(null);
  // 预筛选错误状态（用于内联反馈）
  const [prescreenError, setPrescreenError] = useState<PrescreenErrorState>({
    visible: false,
    category: null,
    message: null,
  });
  // Soft Pass 状态（低置信度但放行）
  const [softPassState, setSoftPassState] = useState<SoftPassState>({
    visible: false,
    tooltipMessage: "",
  });

  // 上传处理 - 保存图片信息并立即进行预筛选
  // 基于"最小打扰"原则：成功时静默处理，失败时显示内联反馈
  const handleUpload = useCallback(
    async (
      blob: Blob,
      fileName: string,
      diagnostics: ImageDiagnostics,
      previewUrl: string,
    ) => {
      // 先清除之前的错误状态（处理竞态条件）
      setPrescreenError({ visible: false, category: null, message: null });
      setSoftPassState({ visible: false, tooltipMessage: "" });

      // 显示预览图
      setLocalPreviewUrl(previewUrl);
      setIsPrescreening(true);
      setPrescreenResult(null);

      try {
        // 创建 File 对象用于预筛选
        const file = new File([blob], fileName, {
          type: blob.type || "image/webp",
        });

        // 调用预筛选 API
        const result = await assessmentService.prescreenImage({ image: file });

        // 检查是否为未授权错误 - 跳转到登录页
        if (
          !result.passed &&
          "reason" in result &&
          result.reason === "unauthorized"
        ) {
          toast.error(result.message || "请先登录");
          navigate("/login", { state: { from: location } });
          return;
        }

        // 检查响应是否有效（可能是 422 等错误返回的非标准格式）
        if (result && typeof result.passed === "boolean") {
          if (result.passed) {
            // 预筛选通过，保存图片信息（静默处理，不弹 Toast）
            setUploadedBlob(blob);
            setUploadedFileName(fileName);
            setUploadedDiagnostics(diagnostics);
            setPrescreenResult(result);

            // 检查是否为 Soft Pass（低置信度但放行）
            if (isSoftPass(result)) {
              setSoftPassState({
                visible: true,
                tooltipMessage: t("prescreen.softPass.tooltip"),
              });
            }
            // 成功时不显示任何 Toast，直接流转到下一步
          } else {
            // 预筛选失败，检查是否需要显示内联反馈
            if (shouldShowFeedback(result)) {
              const category = mapReasonToCategory(result.reason);
              if (category) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const translateFn = (
                  key: string,
                  opts?: Record<string, unknown>,
                ) => t(key, opts as any) as string;
                const message = getLocalizedMessage(category, translateFn);
                setPrescreenError({
                  visible: true,
                  category,
                  message,
                });
                // 预筛选失败时再次弹出示例提示，配合 useEffect 4s 后自动消失
                setShowPhotoExamples(true);
              }
            }
            // 不清除预览图，让用户看到错误遮罩
            // 但不保存图片信息
            setUploadedBlob(null);
            setUploadedFileName("");
            setUploadedDiagnostics(null);
          }
        } else {
          // 响应格式不正确（可能是 422 等错误），应用 Fail Open 策略
          console.warn(
            "[Prescreen] Invalid response format, applying fail-open:",
            result,
          );
          setUploadedBlob(blob);
          setUploadedFileName(fileName);
          setUploadedDiagnostics(diagnostics);
        }
      } catch (error) {
        // 网络错误等，降级处理：静默允许继续（Fail Open 策略）
        console.warn(
          "[Prescreen] Service unavailable, applying fail-open:",
          error,
        );

        // 保存图片信息，允许继续（不弹 Toast）
        setUploadedBlob(blob);
        setUploadedFileName(fileName);
        setUploadedDiagnostics(diagnostics);
      } finally {
        setIsPrescreening(false);
      }
    },
    [t, navigate, location],
  );

  /**
   * 开始诊断 - 提交图片进行诊断
   * @see Requirements 1.3, 4.2 - 使用 /api/v1/assessments 端点
   * @see Requirements 2.1 - 包含 tank_id 参数
   */

  // 包装提交逻辑
  const handleStartAssessment = useCallback(async () => {
    if (!uploadedBlob || !uploadedFileName) {
      toast.error("请先上传图片");
      return;
    }

    // 防御性检查: 余额不足
    if (balance < ASSESSMENT_COST) {
      // 构造一个模拟的 402 错误数据来触发弹窗
      setInsufficientPointsData({
        required: ASSESSMENT_COST,
        shortage: ASSESSMENT_COST - balance,
        suggested_actions: [], // 这里可以不传，或者传默认动作
      } as unknown as InsufficientPointsErrorData);
      setShowInsufficientPointsModal(true);
      return;
    }

    const file = new File([uploadedBlob], uploadedFileName, {
      type: uploadedBlob.type || "image/webp",
    });

    // 生成幂等性 Key
    const idempotencyKey = self.crypto.randomUUID();

    createAssessmentMutation.mutate({
      image: file,
      user_query: userQuery.trim() || undefined,
      toolbox_items:
        selectedToolboxItems.length > 0
          ? JSON.stringify(selectedToolboxItems)
          : undefined,
      tank_id: selectedTank?.id,
      idempotencyKey,
    });
  }, [
    uploadedBlob,
    uploadedFileName,
    balance,
    userQuery,
    selectedToolboxItems,
    selectedTank,
    createAssessmentMutation,
  ]);

  // 替换原来的 isSubmitting 状态
  // 注意：我们可能还需要保留一个本地状态或者直接使用 mutation 的状态
  // 这里我们把 mutation.isPending 作为 isSubmitting 的一部分
  // 但要注意原来的 isSubmitting 可能是为了其他 UI 效果，这里可以做一个别名
  const isCreatingAssessment = createAssessmentMutation.isPending;

  // 解析诊断结果
  // 优先使用后端映射后的顶层 stage_2_public，否则回退到 analysis_result 解析
  const parsedAssessment = useMemo(() => {
    if (!currentAssessment) return null;

    // 调试日志：帮助排查第三张卡片不显示的问题
    if (process.env.NODE_ENV === "development") {
      console.log("[parsedAssessment] currentAssessment:", {
        status: currentAssessment.status,
        hasStage2: !!currentAssessment.stage_2_public,
        hasAnalysisResult: !!currentAssessment.analysis_result,
        hasStage3: !!currentAssessment.stage_3_private,
      });
    }

    // 优先使用顶层的 stage_2_public（后端已映射好的结构）
    if (currentAssessment.stage_2_public) {
      const stage2 = currentAssessment.stage_2_public;

      // 处理 visual_evidence：确保它是数组
      let visualEvidence: Array<{ body_part: string; symptom: string }> = [];
      if (Array.isArray(stage2.visual_evidence)) {
        visualEvidence = stage2.visual_evidence;
      } else if (typeof stage2.visual_evidence === "string") {
        // 如果是字符串，尝试解析为 JSON
        try {
          const parsed = JSON.parse(stage2.visual_evidence);
          if (Array.isArray(parsed)) {
            visualEvidence = parsed;
          }
        } catch (e) {
          console.warn("Failed to parse visual_evidence as JSON:", e);
        }
      }

      return {
        title: stage2.assessment_title,
        severity: stage2.severity_level,
        confidence: stage2.confidence_score,
        summary: stage2.analysis_summary,
        findings: visualEvidence.map((e) => `${e.body_part}: ${e.symptom}`),
        stage_2_public: stage2,
        stage_3_private: currentAssessment.stage_3_private,
        cached: currentAssessment.cached,
        cached_from_id: currentAssessment.cached_from_id,
      } as ParsedAssessment;
    }

    // 回退：从 analysis_result 解析（兼容旧数据）
    const parsed = tryParseJson(currentAssessment.analysis_result);

    // 如果 analysis_result 也无法解析，但状态是 completed，返回一个最小的有效对象
    // 这确保 hasResult 能正确设为 true，用户可以看到基本的完成状态
    if (
      !parsed &&
      (currentAssessment.status === "completed" ||
        currentAssessment.status === "unlocked_care_plan")
    ) {
      console.warn(
        "[parsedAssessment] Status is completed but no parseable data, returning minimal object",
      );
      return {
        title: "评估完成",
        severity: "low" as const,
        confidence: 0,
        summary: "",
        findings: [],
        stage_2_public: undefined,
        stage_3_private: currentAssessment.stage_3_private,
      } as ParsedAssessment;
    }

    return parsed;
  }, [currentAssessment]);

  const hasResult =
    (currentAssessment?.status === "completed" ||
      currentAssessment?.status === "unlocked_care_plan" ||
      (currentAssessment?.status === "failed" &&
        !!currentAssessment?.analysis_result)) &&
    !!parsedAssessment;

  // isProcessing 只在有 currentAssessment 或正在提交时为 true
  // 但第二阶段卡片只在有 currentAssessment 时显示
  const isProcessing =
    currentAssessment?.status === "processing" ||
    currentAssessment?.status === "pending" ||
    currentAssessment?.status === "pending_analysis" ||
    currentAssessment?.status === "pending_prescreen";
  // 是否正在提交（用于显示扫描效果和处理状态文案）
  const isAnalyzing = isSubmitting || isProcessing;

  // 病灶区域（从 AI 返回的坐标）
  const lesionAreas = useMemo(() => {
    const evidence = parsedAssessment?.stage_2_public?.visual_evidence;
    if (!evidence || !Array.isArray(evidence)) return [];

    return evidence
      .filter(
        (e) => typeof e.bbox_x === "number" && typeof e.bbox_y === "number",
      )
      .map((e, idx) => ({
        x: e.bbox_x as number,
        y: e.bbox_y as number,
        label: e.body_part || `病灶 ${idx + 1}`,
      }));
  }, [parsedAssessment]);

  // Conversion Funnel: Timeline 预览数据

  // 重置为新建状态
  const handleNewAssessment = useCallback(() => {
    setSelectedId("new");
    setIsNewUpload(false);
    setUploadedBlob(null);
    setUploadedFileName("");
    setUploadedDiagnostics(null);
    setLocalPreviewUrl(null);
    setUserQuery("");
    setPrescreenResult(null);
  }, []);

  /**
   * 处理鱼缸选择
   * @see Requirements 1.4
   */
  const handleTankSelect = useCallback((tank: Tank) => {
    setSelectedTank(tank);
    setShowTankModal(false);
    setIsTankChangeMode(false);
  }, []);

  /**
   * 跳过鱼缸选择
   * @see Requirements 1.7
   */
  const handleSkipTankSelection = useCallback(() => {
    setShowTankModal(false);
    setIsTankChangeMode(false);
  }, []);

  /**
   * 打开鱼缸选择 Modal（首次选择或更换）
   * @see Requirements 1.2
   */
  const handleOpenTankModal = useCallback(() => {
    const isChange = !!selectedTank;
    // 先设置模式，再打开 Modal，确保 Modal 渲染时能获取正确的模式
    setIsTankChangeMode(isChange);
    // 使用 setTimeout 确保状态更新后再打开 Modal
    setTimeout(() => {
      setShowTankModal(true);
    }, 0);
  }, [selectedTank]);

  /**
   * 重新选择鱼缸（更换模式）
   * @see Requirements 1.6
   */
  const handleChangeTank = useCallback(() => {
    setIsTankChangeMode(true);
    setTimeout(() => {
      setShowTankModal(true);
    }, 0);
  }, []);

  /**
   * 清除已选鱼缸
   */
  const handleClearTank = useCallback(() => {
    setSelectedTank(null);
  }, []);

  /**
   * 计算是否可以删除图片
   * 条件：有本地预览图片 && 新建模式 && 未在分析中
   * @see Requirements 1.1, 1.2, 1.3
   */
  const canClearImage = useMemo(() => {
    const hasLocalImage = !!localPreviewUrl;
    const isNewMode = selectedId === "new";
    const notAnalyzing = !isSubmitting && !isProcessing;
    return hasLocalImage && isNewMode && notAnalyzing;
  }, [localPreviewUrl, selectedId, isSubmitting, isProcessing]);

  /**
   * 清除当前选择的图片
   */
  const handleClearImage = useCallback(() => {
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    setLocalPreviewUrl(null);
    setUploadedBlob(null);
    setUploadedFileName("");
    setPrescreenResult(null);
    setPrescreenError({ visible: false, category: null, message: null });
    setSoftPassState({ visible: false, tooltipMessage: "" });
  }, [localPreviewUrl]);

  /**
   * 重试预筛选（用于可重试的错误，如网络问题）
   */
  const handleRetryPrescreen = useCallback(() => {
    // 清除错误状态
    setPrescreenError({ visible: false, category: null, message: null });
    // 清除预览，让用户重新上传
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    setLocalPreviewUrl(null);
  }, [localPreviewUrl]);

  /**
   * 重新选择图片（用于不可重试的错误，如非鱼类图片）
   */
  const handleReselectImage = useCallback(() => {
    // 清除所有状态
    setPrescreenError({ visible: false, category: null, message: null });
    setSoftPassState({ visible: false, tooltipMessage: "" });
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    setLocalPreviewUrl(null);
    setUploadedBlob(null);
    setUploadedFileName("");
    setPrescreenResult(null);
  }, [localPreviewUrl]);

  /**
   * 解锁护理方案
   * @see Requirements 4.5
   */
  const handleUnlockCarePlan = useCallback(async () => {
    // 如果已解锁，直接显示
    if (parsedAssessment?.stage_3_private) {
      setShowTimeline(true);
      return;
    }

    if (!currentAssessment?.friendly_id) return;

    setIsUnlocking(true);
    try {
      const { data } = await assessmentService.unlockCarePlan(
        currentAssessment.friendly_id,
      );

      // 显示成功提示（如果后端返回了 promotion 信息）
      if (data.unlock_info?.promotion) {
        toast.success(data.message);
      }

      // 使得当前查询失效以刷新数据（获取 care_unlocked_at）
      await queryClient.invalidateQueries({
        queryKey: ["assessment", selectedId],
      });

      // 显示时间轴
      setShowTimeline(true);
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 402) {
        setInsufficientPointsData(err.data as InsufficientPointsErrorData);
        setShowInsufficientPointsModal(true);
      } else {
        toast.error(err instanceof Error ? err.message : "解锁失败");
      }
    } finally {
      setIsUnlocking(false);
    }
  }, [currentAssessment, parsedAssessment, queryClient, selectedId]);

  /**
   * 计算当前诊断阶段
   * @see Requirements 3.2 - 阶段状态推导逻辑
   */
  const currentStage = useMemo((): AssessmentStage => {
    // 显示护理时间轴时
    if (showTimeline) return "carePlan";

    // 新建诊断模式
    if (selectedId === "new") {
      if (isSubmitting) return "submitting";
      if (isPrescreening) return "prescreening";
      if (prescreenError?.visible) return "prescreenFailed";
      if (localPreviewUrl) return "uploading";
      return "initial";
    }

    // 查看历史诊断 - 使用 loading 状态区分加载历史记录和 AI 分析中
    // 只要有本地预览图 (localPreviewUrl)，就认为是在进行新的分析流程，哪怕 MD5 命中了缓存
    if (loadingAssessment)
      return isNewUpload || !!localPreviewUrl ? "processing" : "loading";
    if (!currentAssessment) return "initial";

    switch (currentAssessment.status) {
      case "pending":
      case "processing":
      case "pending_analysis":
      case "pending_prescreen":
        return "processing";
      case "completed":
      case "unlocked_care_plan":
        return currentAssessment.analysis_result ||
          currentAssessment.stage_2_public
          ? "completed"
          : "failed";
      case "failed":
      case "timeout_error":
        // 如果有分析结果，仍然显示为完成状态
        return currentAssessment.analysis_result ||
          currentAssessment.stage_2_public
          ? "completed"
          : "failed";
      default:
        return "initial";
    }
  }, [
    selectedId,
    isSubmitting,
    isPrescreening,
    prescreenError?.visible,
    localPreviewUrl,
    loadingAssessment,
    currentAssessment,
    showTimeline,
    isNewUpload,
  ]);

  // 创建处理状态组件（用于在图片中央显示）
  const processingStatusComponent = useMemo(() => {
    if (!isProcessing) return undefined;
    return (
      <div className="bg-black/70 backdrop-blur-md rounded-2xl px-6 py-4 shadow-2xl">
        <ProcessingStatus toolboxItemsCount={selectedToolboxItems.length} />
      </div>
    );
  }, [isProcessing, selectedToolboxItems.length]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col relative overflow-x-hidden w-full">
      {/* 顶部打字机提示 - 移动端减少顶部间距 */}
      <TypewriterHint
        stage={currentStage}
        className="flex-shrink-0 pt-2 pb-2 px-3 sm:px-4 mt-8 sm:mt-12"
        ready={typewriterReady}
      />

      {/* 鱼缸选择 Modal - 用户可选打开 */}
      {/* @see Requirements 1.2, 1.4, 1.7 */}
      <TankSelectionModal
        open={showTankModal}
        onSelect={handleTankSelect}
        onSkip={handleSkipTankSelection}
        onClose={() => {
          setShowTankModal(false);
          setIsTankChangeMode(false);
        }}
        isChangeMode={isTankChangeMode}
      />

      {/* 历史记录面板 - 从左侧 fade in */}
      <HistoryPanel
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        items={historyRecords}
        loading={loadingHistory}
        selectedId={selectedId}
        onSelect={(id) => {
          setIsNewUpload(false);
          setSelectedId(id);
          if (id === "new") {
            setUploadedBlob(null);
            setUploadedFileName("");
            setLocalPreviewUrl(null);
          } else {
            // 切换到历史记录时，清除本地预览，使用历史记录的图片
            setLocalPreviewUrl(null);
            setUploadedBlob(null);
            setUploadedFileName("");
          }
        }}
        onDelete={(id) => deleteMutation.mutate(id)}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />

      {/* 主内容区 - 固定布局，子元素各自定位 */}
      <div className="flex-1 relative overflow-auto flex flex-col">
        <AnimatePresence mode="wait">
          {/* 流水线卡片区 - 3 个卡片横向排列，垂直居中 */}
          {!showTimeline && (
            <motion.div
              key="pipeline-cards"
              layout
              initial={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{
                layout: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
                opacity: { duration: 0.4 },
                x: { duration: 0.4, ease: "easeOut" },
              }}
              className={cn(
                "w-full max-w-[1400px] mx-auto flex flex-col md:flex-row items-center md:items-center justify-center gap-0 px-4 sm:px-6 pb-32 flex-grow",
              )}
            >
              {/* 404 错误状态 */}
              {isAssessmentNotFound && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex flex-col items-center justify-center w-full max-w-md mx-auto py-16"
                >
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-muted-foreground"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold">记录不存在</h3>
                    <p className="text-muted-foreground text-sm max-w-xs mx-auto">
                      该评估记录不存在或已被删除
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsNewUpload(false);
                        setSelectedId("new");
                        navigate("/assessments/new", { replace: true });
                      }}
                    >
                      创建新评估
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* 窗口1：上传 + 工具箱 */}
              <motion.div
                className="relative flex flex-col items-center w-full md:w-auto"
                layout
                transition={{
                  layout: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
                }}
              >
                {/* 上传卡片 - 始终显示，无需先选择鱼缸 */}
                {/* @see Requirements 1.1 */}
                <UploadCard
                  imageUrl={currentAssessment?.image_url}
                  localPreviewUrl={localPreviewUrl}
                  isProcessing={isProcessing}
                  isPrescreening={isPrescreening}
                  isLoading={loadingAssessment && selectedId !== "new"}
                  onUpload={handleUpload}
                  onClearImage={handleClearImage}
                  lesionAreas={lesionAreas}
                  highlightLesions={highlightedFeature !== null}
                  canClearImage={canClearImage && !isPrescreening}
                  prescreenError={prescreenError}
                  onRetry={handleRetryPrescreen}
                  onReselect={handleReselectImage}
                  softPassState={softPassState}
                  processingStatusComponent={processingStatusComponent}
                />

                {/* 智能药箱选择器 + 开始分析按钮 / 处理状态 - 自然流布局 */}
                {/* 只在需要显示表单时才渲染容器 */}
                {selectedId === "new" &&
                  !isSubmitting &&
                  !currentAssessment &&
                  localPreviewUrl &&
                  !isPrescreening &&
                  !prescreenError?.visible && (
                    <div className="mt-6 w-full md:w-[320px] lg:w-[360px] space-y-4">
                      <AnimatePresence mode="wait">
                        <AssessmentInputForm
                          selectedTank={selectedTank}
                          onTankSelect={handleTankSelect}
                          selectedToolboxItems={selectedToolboxItems}
                          onToolboxChange={setSelectedToolboxItems}
                          userQuery={userQuery}
                          onUserQueryChange={setUserQuery}
                          onStartAnalysis={handleStartAssessment}
                          disabled={isSubmitting}
                          showStartButton={!!uploadedBlob}
                          assessmentCost={ASSESSMENT_COST}
                        />
                      </AnimatePresence>
                    </div>
                  )}
              </motion.div>

              {/* 连接线 1-2 - 只在有分析记录且非失败状态时显示 */}
              {currentAssessment &&
                currentAssessment.status !== "failed" &&
                currentAssessment.status !== "timeout_error" && (
                  <Connector completed={hasResult} animate={isProcessing} />
                )}

              {/* 窗口2：视觉扫描结果 - 只在有分析记录且非失败状态时显示 */}
              <AnimatePresence>
                {currentAssessment &&
                  currentAssessment.status !== "failed" &&
                  currentAssessment.status !== "timeout_error" && (
                    <VisualScanCard
                      assessmentId={currentAssessment.friendly_id}
                      fishSpecies={
                        parsedAssessment?.stage_2_public?.fish_species
                      }
                      confidence={parsedAssessment?.confidence}
                      visualEvidence={
                        parsedAssessment?.stage_2_public?.visual_evidence || []
                      }
                      visualSummary={
                        parsedAssessment?.stage_2_public?.analysis_summary
                      }
                      isLoading={isProcessing}
                      onHoverFeature={setHighlightedFeature}
                      initialFeedbackStatus={currentAssessment.feedback_status}
                    />
                  )}
              </AnimatePresence>

              {/* 连接线 2-3 */}
              {hasResult && <Connector completed={true} animate={false} />}

              {/* 窗口3：健康评估与风险等级卡片 */}
              {hasResult && (
                <AssessmentCard
                  assessmentTitle={parsedAssessment?.title}
                  diseaseCode={
                    parsedAssessment?.stage_2_public?.detected_disease_code
                  }
                  severity={parsedAssessment?.severity}
                  rootCauses={parsedAssessment?.stage_3_private?.root_causes}
                  urgencyTeaser={
                    parsedAssessment?.stage_2_public?.urgency_teaser
                  }
                  sopSource={parsedAssessment?.stage_3_private?.sop_source}
                  readinessScore={
                    parsedAssessment?.stage_3_private?.readiness_score
                  }
                  isLoading={false}
                  waterType={currentAssessment.tank_water_type}
                />
              )}
            </motion.div>
          )}

          {/* 完整的护理时间轴面板 - 替换整个 3 卡片区域 */}
          {showTimeline && (
            <CarePlanTimelinePanel
              assessmentId={selectedId !== "new" ? selectedId : ""}
              currentAssessment={currentAssessment}
              parsedAssessment={parsedAssessment}
              selectedToolboxItems={selectedToolboxItems}
              onBack={() => setShowTimeline(false)}
            />
          )}
        </AnimatePresence>
      </div>

      {/* 底部功能栏 - 历史记录入口 */}
      <BottomActionBar
        visible={!currentAssessment && !showTimeline && !isPrescreening}
      >
        {/* 照片示例面板 - 移动到按钮上方 */}
        <AnimatePresence>
          {!localPreviewUrl && showPhotoExamples && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="w-full mb-3"
            >
              <PhotoExamples onClose={() => setShowPhotoExamples(false)} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* 历史记录按钮 - 仅在未上传图片时显示 */}
        {!localPreviewUrl && (
          <Button
            variant="outline"
            size="lg"
            className="min-w-[240px] h-12 rounded-full shadow-sm bg-background/80 backdrop-blur-md border-border/60 hover:bg-accent/50 text-sm font-medium transition-all duration-300 group"
            onClick={() => setShowHistory(true)}
          >
            <History className="h-5 w-5 mr-1.5 text-muted-foreground group-hover:text-foreground transition-colors" />
            <span className="text-muted-foreground group-hover:text-foreground transition-colors">
              {t("pipeline.history.title")}
            </span>
          </Button>
        )}

        {/* 免责声明 - 整合在底部栏中 */}
        <p className="text-[10px] text-muted-foreground/40 text-center mt-1 w-full px-4 break-words">
          {t("pipeline.disclaimer")}
        </p>
      </BottomActionBar>

      {/* 底部固定 CTA - 情境感知按钮 (SmartCTABar 内部也是类似的 fixed 布局，这里不冲突，因为 show 状态互斥) */}
      <AnimatePresence>
        {hasResult && selectedId !== "new" && !showTimeline && (
          <SmartCTABar
            severity={parsedAssessment?.severity || "unknown"}
            onAction={handleUnlockCarePlan}
            isLoading={isUnlocking}
          />
        )}
      </AnimatePresence>

      {/* 积分不足弹窗 */}
      <InsufficientPointsDialog
        open={showInsufficientPointsModal}
        onOpenChange={setShowInsufficientPointsModal}
        errorData={insufficientPointsData}
      />
    </div>
  );
};

export default AssessmentPipeline;
